/**
 * Request Enums
 *
 * Domain types for employee requests. Hosted in `@shared/types` so the shared
 * i18n layer can consume them without depending on a feature.
 *
 * All canonical definitions live in `@shared/models/requestTypes` and are
 * re-exported here. The legacy Norwegian-valued enums have been replaced by
 * the API string-union types + const objects (type and value share the same
 * name, e.g. `RequestStatus.DRAFT`).
 */

/**
 * Source of a request (API spec). The canonical value stored on `Request.source`.
 * Re-exported (value + type) from `@shared/models/requestTypes`.
 */
export { RequestSource } from '@shared/models/requestTypes';

/**
 * UI request categories (grouping of `RequestSource` for label/icon/color).
 * Re-exported (value + type) from `@shared/models/requestTypes`.
 *
 * VACATION ← VACATION/HOLIDAYS/WEEKEND, PAID_LEAVE ← PAID_LEAVE,
 * SHIFT_PREFERENCE ← WISH (see `sourceToRequestType`).
 */
export { RequestType } from '@shared/models/requestTypes';

/**
 * Possible statuses of a request (API spec + local state).
 *
 * `CANCELLED` is NOT part of the API spec; it is a local/offline state used
 * by the client to represent a submitted request cancelled before sync.
 *
 * Re-exported from `@shared/models/requestTypes` so there is a single
 * canonical `RequestStatus` (both type and value constants) across the app.
 * A single `export` carries both the value and the type binding.
 */
export { RequestStatus } from '@shared/models/requestTypes';

/**
 * Shift preference options (semantic keys → API segment codes).
 *
 * `ShiftPreference` is now an alias of the canonical `SegmentType` (type +
 * const object) defined in `@shared/models/requestTypes`. Both the type and
 * the runtime values are re-exported here so existing feature imports keep
 * working. Prefer `SegmentType` in new code.
 *
 * D = Morgen (Morning), A = Ettermiddag (Afternoon), N = Kveld (Night),
 * L = Helg (Weekend), X = Annet (Other)
 */
export { SegmentType as ShiftPreference } from '@shared/models/requestTypes';

/**
 * Severity levels for conflicts
 * Feil = Error (blocking)
 * Advarsel = Warning (non-blocking, requires confirmation)
 */
export enum ConflictSeverity {
    ERROR = 'Feil',
    WARNING = 'Advarsel',
}

/**
 * UI request category keys (for label/icon/color lookups).
 * Same as `RequestType` (the UI grouping of `RequestSource`).
 */
import type { RequestType } from '@shared/models/requestTypes';
export type RequestTypeKey = RequestType;
